import React, { useState, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import NtText from "../../components/NtText";
import NtTextInput from "../../components/NtTextInput";
import NtButton from "../../components/NtButton";
import NtCodeInput from "../../components/NtCodeInput";
import { getAuthService } from "@ntconnect/framework";
import { showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import routes from "../../navigation/routes";
import { navigationRef } from "../../../App";
import { fetchPermissions } from "../../api/rbac";
import { useTheme } from "../../context/ThemeContext";
import BaseAuthController from "./BaseAuthController";
import NtVoxcraftBrand from "../../marketing/components/voxcraft/NtVoxcraftBrand";
import settings from "../../config/settings";
import {
  isValidPassword,
  MIN_PASSWORD_LENGTH,
} from "../../onboarding/utils/validationUtils";

// 60s resend cooldown matches the BFF's 1/min/(domain,identifier) limit.
const RESEND_COOLDOWN_MS = 60_000;

class PasswordRecoveryController extends BaseAuthController {
  state = {
    ...super.state,
    step: 1, // 1 = request, 2 = confirm
    domain: this.props.route?.params?.domain || settings.defaultDomain || "",
    identifier: this.props.route?.params?.identifier || "",
    sentTo: null,
    expiresAt: null,
    code: "",
    newPassword: "",
    confirmPassword: "",
    resendDisabledUntil: null,
    loading: false,
    ambiguousEmail: false,
    formError: null,
  };

  componentDidMount() {
    super.componentDidMount && super.componentDidMount();
    if (typeof document !== "undefined") {
      document.title = "Voxcraft | Reset password";
    }
  }

  // ---- Step 1 -----------------------------------------------------
  isStep1Valid = () => {
    const { domain, identifier } = this.state;
    return !!(domain && domain.trim() && identifier && identifier.trim());
  };

  handleRequestCode = async () => {
    if (this.state.loading) return;
    const domain = this.state.domain.trim();
    const identifier = this.state.identifier.trim();
    this.setState({ loading: true, formError: null, ambiguousEmail: false });
    try {
      const res = await getAuthService().businessRequestPasswordReset(domain, identifier);
      Logger("PasswordRecovery · requestCode", res);
      this.setState({
        step: 2,
        sentTo: res.sentTo || null,
        expiresAt: res.expiresAt || null,
        resendDisabledUntil: Date.now() + RESEND_COOLDOWN_MS,
      });
    } catch (e) {
      Logger("PasswordRecovery · requestCode failed", e);
      const msg = String(e?.message || "");
      if (/multiple accounts/i.test(msg)) {
        this.setState({ ambiguousEmail: true, formError: msg });
      } else if (/too[_ ]many|rate/i.test(msg)) {
        this.setState({ formError: "Too many attempts — wait a few minutes." });
      } else {
        showToast("Couldn't send code", msg || "Try again later.", "error");
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  handleResend = async () => {
    if (this.state.loading) return;
    this.setState({ loading: true, formError: null });
    try {
      const res = await getAuthService().businessRequestPasswordReset(
        this.state.domain.trim(),
        this.state.identifier.trim(),
      );
      Logger("PasswordRecovery · resend", res);
      this.setState({
        sentTo: res.sentTo || this.state.sentTo,
        expiresAt: res.expiresAt || this.state.expiresAt,
        resendDisabledUntil: Date.now() + RESEND_COOLDOWN_MS,
      });
      showToast("Code resent", "Check your inbox for the new code.", "info");
    } catch (e) {
      const msg = String(e?.message || "");
      if (/too[_ ]many|rate/i.test(msg)) {
        showToast("Slow down", "Too many attempts — wait a few minutes.", "error");
      } else {
        showToast("Couldn't resend", msg || "Try again later.", "error");
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  // ---- Step 2 -----------------------------------------------------
  isStep2Valid = () => {
    const { code, newPassword, confirmPassword } = this.state;
    return !!(
      /^\d{6}$/.test((code || "").trim()) &&
      isValidPassword(newPassword) &&
      newPassword === confirmPassword
    );
  };

  handleConfirm = async () => {
    if (this.state.loading) return;
    if (!this.isStep2Valid()) {
      this.setState({
        formError:
          "Make sure the code is 6 digits, the password is at least " +
          MIN_PASSWORD_LENGTH +
          " characters, and both passwords match.",
      });
      return;
    }
    const domain = this.state.domain.trim();
    const identifier = this.state.identifier.trim();
    const code = this.state.code.trim();
    const newPassword = this.state.newPassword;

    this.setState({ loading: true, formError: null });
    try {
      const res = await getAuthService().businessConfirmPasswordReset(
        { domain, identifier, code, newPassword },
        { deviceName: deriveDeviceName(), platform: "web" },
      );
      Logger("PasswordRecovery · confirm", res);

      // The framework already persisted accessToken/refreshToken/deviceId.
      // Build the BusinessUser shape so the rest of the app picks up the
      // session without a follow-up /auth/login.
      const claims = decodeJwtClaims(res.tokens.accessToken);
      const businessUser = {
        schema: "business",
        userId: String(claims.sub || claims.userId || ""),
        username: identifier.includes("@") ? "" : identifier,
        displayName: identifier,
        accountId: identifier,
        token: res.tokens.accessToken,
        domain,
        tenant: res.tenant?.id || String(claims.tenantId || ""),
        email: identifier.includes("@") ? identifier : "",
        role: String(claims.role || ""),
      };

      const permissions = await fetchPermissions(domain, businessUser.userId, 1, 1000000);

      this.context.updateUser({
        ...this.context.user,
        ...businessUser,
        authenticated: true,
        permissions: permissions.ok ? permissions.data?.data : [],
      });

      showToast(
        "Password updated",
        "We've signed you in with your new password.",
        "success"
      );
      // Navigator switches automatically once authenticated:true flips.
    } catch (e) {
      Logger("PasswordRecovery · confirm failed", e);
      const msg = String(e?.message || "");
      if (/invalid or expired code/i.test(msg)) {
        this.setState({ formError: "Invalid or expired code." });
      } else if (/too[_ ]many|rate/i.test(msg)) {
        this.setState({ formError: "Too many attempts — wait a few minutes." });
      } else {
        this.setState({ formError: msg || "Could not reset password." });
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  handleBack = () => {
    if (this.state.loading) return;
    if (this.state.step === 2) {
      this.setState({ step: 1, formError: null });
    } else if (navigationRef.current) {
      navigationRef.current.navigate(routes.auth);
    }
  };

  render() {
    const { step } = this.state;
    return (
      <PageWrap>
        <AccentBar />
        <View style={{ alignItems: "flex-start", marginBottom: 14 }}>
          <NtVoxcraftBrand size={30} fontSize={20} />
        </View>

        <ThemedTitle>
          {step === 1 ? "Reset your password" : "Enter the code we sent"}
        </ThemedTitle>
        <ThemedSubtitle style={{ marginBottom: 18 }}>
          {step === 1
            ? "Enter your company alias and your username or email. We'll send a 6-digit code to the email on file."
            : this.state.sentTo
            ? `We sent a 6-digit code to ${this.state.sentTo}. It's good for ~15 minutes.`
            : "We sent a 6-digit code to the email on file."}
        </ThemedSubtitle>

        {step === 1 ? (
          <Step1Form
            domain={this.state.domain}
            identifier={this.state.identifier}
            ambiguousEmail={this.state.ambiguousEmail}
            formError={this.state.formError}
            loading={this.state.loading}
            onChangeDomain={(text) => this.setState({ domain: text })}
            onChangeIdentifier={(text) => this.setState({ identifier: text })}
            onSubmit={this.isStep1Valid() ? this.handleRequestCode : null}
          />
        ) : (
          <Step2Form
            code={this.state.code}
            newPassword={this.state.newPassword}
            confirmPassword={this.state.confirmPassword}
            formError={this.state.formError}
            loading={this.state.loading}
            resendDisabledUntil={this.state.resendDisabledUntil}
            onChangeCode={(code) => this.setState({ code, formError: null })}
            onChangeNewPassword={(text) => this.setState({ newPassword: text })}
            onChangeConfirmPassword={(text) => this.setState({ confirmPassword: text })}
            onSubmit={this.isStep2Valid() ? this.handleConfirm : null}
            onResend={this.handleResend}
          />
        )}

        <FooterRow onBack={this.handleBack} step={step} disabled={this.state.loading} />
      </PageWrap>
    );
  }
}

// ============================================================================
// Theme-aware sub-components (hooks ok here, the parent is a class)
// ============================================================================

function PageWrap({ children }) {
  const { theme } = useTheme();
  return (
    <View style={[styles.page, { backgroundColor: theme.bg }]}>
      <View
        style={[
          styles.card,
          { backgroundColor: theme.surface, borderColor: theme.borderSoft },
        ]}
      >
        {children}
      </View>
    </View>
  );
}

function ThemedTitle({ children }) {
  const { theme } = useTheme();
  return <NtText style={[styles.title, { color: theme.text }]}>{children}</NtText>;
}

function ThemedSubtitle({ children, style }) {
  const { theme } = useTheme();
  return (
    <NtText style={[styles.subtitle, { color: theme.textDim }, style]}>{children}</NtText>
  );
}

function AccentBar() {
  const { theme } = useTheme();
  return <View style={[styles.accentBar, { backgroundColor: theme.brand }]} />;
}

function Step1Form({
  domain,
  identifier,
  ambiguousEmail,
  formError,
  loading,
  onChangeDomain,
  onChangeIdentifier,
  onSubmit,
}) {
  const { theme } = useTheme();
  return (
    <>
      {settings.defaultDomain ? null : (
        <NtTextInput
          containerStyle={{ marginTop: 8 }}
          placeholder="Company alias"
          value={domain}
          autoCorrect={false}
          autoCapitalize="none"
          onChangeText={onChangeDomain}
        />
      )}
      <NtTextInput
        containerStyle={{ marginTop: 12 }}
        placeholder={ambiguousEmail ? "Try your username" : "Username or email"}
        value={identifier}
        autoCorrect={false}
        autoCapitalize="none"
        onChangeText={onChangeIdentifier}
      />

      {!!formError && (
        <NtText style={[styles.error, { color: theme.red }]}>{formError}</NtText>
      )}

      <NtButton
        containerStyle={{ marginTop: 20 }}
        text="Send code"
        icon="email-send-outline"
        onPress={onSubmit}
        isLoading={loading}
      />

      <NtText style={[styles.privacyNote, { color: theme.textMute }]}>
        For security, we'll always say we've sent a code — even if the account
        doesn't exist.
      </NtText>
    </>
  );
}

function Step2Form({
  code,
  newPassword,
  confirmPassword,
  formError,
  loading,
  resendDisabledUntil,
  onChangeCode,
  onChangeNewPassword,
  onChangeConfirmPassword,
  onSubmit,
  onResend,
}) {
  const { theme } = useTheme();
  // Tick each second while the cooldown is active so the countdown decrements
  // and flips to the active button at zero (values below read `now`, not
  // Date.now(), so they update on tick).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!resendDisabledUntil) return;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= resendDisabledUntil) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [resendDisabledUntil]);

  const canResend = !loading && (!resendDisabledUntil || now >= resendDisabledUntil);
  const remainingSec =
    !canResend && resendDisabledUntil
      ? Math.max(0, Math.ceil((resendDisabledUntil - now) / 1000))
      : 0;

  return (
    <>
      <View style={{ marginTop: 12 }}>
        <NtCodeInput length={6} value={code} onChange={onChangeCode} autoFocus />
      </View>

      <NtTextInput
        containerStyle={{ marginTop: 16 }}
        placeholder={`New password (at least ${MIN_PASSWORD_LENGTH} chars)`}
        secureTextEntry
        showEye
        value={newPassword}
        autoCorrect={false}
        autoCapitalize="none"
        onChangeText={onChangeNewPassword}
      />
      <NtTextInput
        containerStyle={{ marginTop: 12 }}
        placeholder="Confirm new password"
        secureTextEntry
        showEye
        value={confirmPassword}
        autoCorrect={false}
        autoCapitalize="none"
        onChangeText={onChangeConfirmPassword}
      />

      {!!formError && (
        <NtText style={[styles.error, { color: theme.red }]}>{formError}</NtText>
      )}

      <NtButton
        containerStyle={{ marginTop: 20 }}
        text="Set new password"
        icon="lock-reset"
        onPress={onSubmit}
        isLoading={loading}
      />

      <View style={styles.resendRow}>
        <NtText style={[styles.resendHint, { color: theme.textDim }]}>
          Didn't get it? Check spam, or
        </NtText>
        <Pressable
          onPress={canResend ? onResend : undefined}
          style={({ pressed }) => [
            { paddingVertical: 2 },
            pressed && canResend && { opacity: 0.7 },
            !canResend && { opacity: 0.5 },
          ]}
        >
          <NtText
            style={[
              styles.resendText,
              canResend
                ? { color: theme.brand2, textDecorationLine: "underline", fontWeight: "600" }
                : { color: theme.textMute },
            ]}
          >
            {canResend ? "resend the code" : `resend in ${remainingSec}s`}
          </NtText>
        </Pressable>
      </View>
    </>
  );
}

function FooterRow({ onBack, step, disabled }) {
  const { theme } = useTheme();
  return (
    <View style={[styles.footerRow, { borderTopColor: theme.borderSoft }]}>
      <Pressable onPress={onBack} disabled={disabled}>
        <NtText style={[styles.linkText, { color: theme.textDim }]}>
          {step === 2 ? "← Back" : "Back to sign in"}
        </NtText>
      </Pressable>
    </View>
  );
}

// ============================================================================
// Helpers — Best-effort device label + JWT claim decoder.
// ============================================================================

function deriveDeviceName() {
  if (typeof navigator === "undefined" || !navigator.userAgent) return undefined;
  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" : "Browser";
  const os =
    /Mac OS X/.test(ua) ? "macOS" :
    /Windows/.test(ua) ? "Windows" :
    /Linux/.test(ua) ? "Linux" :
    /iPhone|iPad/.test(ua) ? "iOS" :
    /Android/.test(ua) ? "Android" : "Web";
  return `${browser} on ${os}`;
}

function decodeJwtClaims(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf8");
    return JSON.parse(json);
  } catch {
    return {};
  }
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    minHeight: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 440,
    borderWidth: 1,
    borderRadius: 14,
    padding: 28,
  },
  accentBar: { height: 3, width: 36, borderRadius: 2, marginBottom: 22 },
  title: { fontSize: 22, fontWeight: "700", letterSpacing: -0.3 },
  subtitle: { fontSize: 13, lineHeight: 19, marginTop: 6 },
  error: { fontSize: 12.5, marginTop: 10 },
  privacyNote: { fontSize: 11, lineHeight: 16, marginTop: 14 },
  resendRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 16,
    gap: 6,
  },
  resendHint: { fontSize: 12.5 },
  resendText: { fontSize: 12.5 },
  footerRow: {
    marginTop: 20,
    paddingTop: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
  },
  linkText: { fontSize: 12.5 },
});

export default PasswordRecoveryController;
